import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { readExpenseParam } from "@/lib/managementDeepLink";
import { api, formatErr, fmtDateTime } from "@/lib/api";
import { HoverTime } from "@/components/HoverTime";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Camera, X, Reply, RefreshCw, ImageOff, CalendarClock, Plus,
  PlayCircle, PauseCircle, CheckCircle2, RotateCcw, UserPlus, Pencil, ShoppingCart, ArrowRight,
} from "lucide-react";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { TicketEditDialog } from "@/components/TicketEditDialog";
import { TicketSearchControl, ticketMatches, ticketInDates, Highlight } from "@/components/TicketSearch";
import { getLang, t as T } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/useAutoTranslate";
// Photos are compressed in-browser before upload — no size rejections.
import { processMediaFiles, isLivePhoto } from "@/lib/livePhotoMedia";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { ColonyBadge, ColonyDotName } from "@/lib/colonyColors";
import { LazyPhotoStrip } from "@/components/PhotoLightbox";
import { WorkerExpensesDialog } from "./WorkerExpensesDialog";
import { TutorialButton } from "@/components/tutorials/TutorialButton";
import { AddToShoppingDialog, ShoppingListView, ReplyShoppingSection, submitShoppingItems, TicketShoppingStrip } from "@/components/ShoppingList";
import { TicketsAiButton } from "@/components/TicketsAiButton";
import { CollapseToggle } from "@/components/TicketCollapse";
import { queueIfOffline } from "@/lib/offlineQueue";
import PhotoAddButtons, { pasteToFiles } from "@/components/PhotoAddButtons";
import { useSelfHeight } from "@/lib/useSelfHeight";
import { useStickyBar } from "@/lib/useStickyBar";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useCalendarPage } from "@/lib/useCalendarPage";
import { CalendarYearSelect } from "@/components/CalendarYearSelect";
import { MobileStatusStrip } from "@/components/MobileStatusStrip";

const STATUS_ORDER = ["open", "in_progress", "hold", "done", "cancelled", "scheduled"];
const STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  open: { label: "Open", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  hold: { label: "On hold", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  done: { label: "Closed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-slate-700 border-rose-300" },
};

export default function WorkerDashboard() {
  const isMobileViewport = useMediaQuery("(max-width: 767px)");
  const stickyOffsetVar = isMobileViewport ? "--worker-mobile-top" : "--sticky-header-h";
  const [filterBarRef, filterBarHeight] = useSelfHeight();
  const [sentinelRef, isStuck] = useStickyBar(stickyOffsetVar);
  const ticketPage = useCalendarPage({ client: api, path: "/worker/tickets", initialTicket: new URLSearchParams(window.location.search).get("ticket") || "" });
  const { items: tickets, setItems: setTickets, reload: reloadTickets, selectTicket: selectTicketYear } = ticketPage;
  const [colonies, setColonies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [colonyFilter, setColonyFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [profileLoading, setLoading] = useState(true);
  const [profileError, setLoadError] = useState("");
  const loading = profileLoading || ticketPage.loading;
  const loadError = profileError || ticketPage.error;
  const [createOpen, setCreateOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [focusExpenseId, setFocusExpenseId] = useState(() => readExpenseParam());
  useEffect(() => {
    if (!focusExpenseId || !me?.is_worker_manager) return;
    setExpensesOpen(true);
    window.history.replaceState({}, "", window.location.pathname);
  }, [focusExpenseId, me?.is_worker_manager]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [scrollToTicketId, setScrollToTicketId] = useState(null);
  const [focusedTicketId, setFocusedTicketId] = useState(null);
  const { setHeaderActions } = useOutletContext() || {};
  // Report issue / Expenses / Shopping list buttons now live in the
  // WorkerLayout header taskbar (icon-only, saves a whole row on
  // phone/tablet) — hand the open-handlers up via Outlet context.
  useEffect(() => {
    setHeaderActions && setHeaderActions({
      workerName: me?.name,
      onReportIssue: () => setFeedbackOpen(true),
      onExpenses: me?.is_worker_manager ? () => setExpensesOpen(true) : null,
      onShoppingList: me?.is_worker_manager ? () => setShoppingOpen(true) : null,
    });
    return () => setHeaderActions && setHeaderActions({});
  }, [me?.is_worker_manager, me?.name, setHeaderActions]);

  const openTicketFromShopping = useCallback((ticketId) => {
    setShoppingOpen(false);
    if (!tickets.some(ticket => ticket.id === ticketId)) selectTicketYear(ticketId);
    setScrollToTicketId(ticketId);
    setFocusedTicketId(ticketId);
    setDateFrom(""); setDateTo("");
    setStatusFilter("all");
    setColonyFilter("all");
    setSearch("");
  }, [tickets, selectTicketYear]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await api.get("/worker/me");
      setColonies(meRes.data?.colonies || []);
      setMe(meRes.data?.user || null);
      setLoadError("");
    } catch (e) {
      const status = e.response?.status;
      setLoadError(
        status === 401 || status === 403
          ? T("Your session has expired — please sign out and sign back in.")
          : (e.response?.data?.detail
              ? formatErr(e.response.data.detail)
              : `${e.message || T("Could not load your tickets.")}${status ? ` (HTTP ${status})` : ""}`)
      );
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(() => Promise.all([loadProfile(), reloadTickets()]), [loadProfile, reloadTickets]);
  useEffect(() => { loadProfile(); }, [loadProfile]);
  // Offline photo review: cache bounded display photos one at a time. The old
  // all-at-once base64 response could exceed iOS Safari's tab memory limit.
  useEffect(() => {
    if (!tickets.length || !navigator.onLine) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const { getCachedValue, setCachedValue, markPrefetched, wasPrefetched } =
        await import("@/lib/photoCache");
      const closed = tickets.filter(
        (t) =>
          (t.status === "done" || t.status === "cancelled") &&
          (t.photo_count || (t.notes || []).some((n) => n.photo_count)),
      );
      for (const t of closed) {
        if (cancelled) break;
        if (await wasPrefetched(t.id)) continue;
        try {
          const { data } = await api.get(`/worker/tickets/${t.id}/photos/manifest`);
          if (cancelled) break;
          if (data.photos?.length) await setCachedValue(`ticket-${t.id}:manifest`, data.photos);
          for (let displayIndex = 0; displayIndex < (data.photos || []).length; displayIndex += 1) {
            if (cancelled) break;
            const item = data.photos[displayIndex];
            const key = `ticket-${t.id}:full:${displayIndex}`;
            if (!await getCachedValue(key)) {
              const response = await api.get(`/worker/tickets/${t.id}/photos/item?source=ticket&index=${item.index}`);
              await setCachedValue(key, response.data.photo);
              await new Promise((resolve) => window.setTimeout(resolve, 150));
            }
          }
          for (const [nid, items] of Object.entries(data.note_photos || {})) {
            if (!items?.length) continue;
            await setCachedValue(`note-${nid}:manifest`, items);
            for (let displayIndex = 0; displayIndex < items.length; displayIndex += 1) {
              if (cancelled) break;
              const key = `note-${nid}:full:${displayIndex}`;
              if (!await getCachedValue(key)) {
                const response = await api.get(`/worker/tickets/${t.id}/photos/item?source=note&note_id=${encodeURIComponent(nid)}&index=${items[displayIndex].index}`);
                await setCachedValue(key, response.data.photo);
                await new Promise((resolve) => window.setTimeout(resolve, 150));
              }
            }
          }
          await markPrefetched(t.id);
        } catch {
          // best-effort — a failed prefetch just retries on the next load
        }
      }
    }, 4000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [tickets]);
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || !tickets.length) return;
    const tid = new URLSearchParams(window.location.search).get("ticket");
    if (!tid) return;
    deepLinkedRef.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    openTicketFromShopping(tid);
  }, [tickets, openTicketFromShopping]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("offline-synced", h);
    return () => window.removeEventListener("offline-synced", h);
  }, [load]);

  const filtered = useMemo(() => {
    const cname = (id) => colonies.find((c) => c.id === id)?.name || "";
    const q = search.trim();
    const opts = { colonyName: cname, statusLabel: (s) => STATUS_META[s]?.label || "" };
    const arr = tickets
      .filter((t) => q ? true : (statusFilter === "all" || t.status === statusFilter))
      .filter((t) => colonyFilter === "all" || t.colony_id === colonyFilter)
      .filter((t) => ticketMatches(t, q, opts))
      .filter((t) => ticketInDates(t, dateFrom, dateTo));
    const byCreatedDesc = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");
    if (sortBy === "oldest") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sortBy === "updated") arr.sort((a, b) => (b.status_updated_at || b.created_at || "").localeCompare(a.status_updated_at || a.created_at || ""));
    else if (sortBy === "colony") arr.sort((a, b) => cname(a.colony_id).localeCompare(cname(b.colony_id)) || byCreatedDesc(a, b));
    else if (sortBy === "scheduled") arr.sort((a, b) => (a.scheduled_for || "9999").localeCompare(b.scheduled_for || "9999") || byCreatedDesc(a, b));
    else arr.sort(byCreatedDesc);
    const pin = (t) => (t.urgent && (t.status === "open" || t.status === "in_progress") ? 1 : 0);
    arr.sort((a, b) => pin(b) - pin(a)); // urgent floats to the top only while active
    return arr;
  }, [tickets, statusFilter, colonyFilter, sortBy, colonies, search, dateFrom, dateTo]);

  // After "Start" jumps the tab to In progress, scroll down to that exact
  // ticket — it may not be at the top once re-sorted into the new list.
  useEffect(() => {
    if (!scrollToTicketId || !filtered.some(ticket => ticket.id === scrollToTicketId)) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`worker-ticket-${scrollToTicketId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFocusedTicketId(scrollToTicketId);
      }
      setScrollToTicketId(null);
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollToTicketId, filtered]);

  const counts = useMemo(() => {
    const c = { all: tickets.length };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const t of tickets) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tickets]);

  // Auto-translate ticket content (titles, details, replies) when Spanish.
  const dynTexts = useMemo(
    () => filtered.flatMap((tk) => [tk.title, tk.description, ...(tk.notes || []).map((n) => n.body)]),
    [filtered]
  );
  const atr = useAutoTranslate(api, dynTexts);

  const colonyName = (id) => colonies.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-5" data-testid="worker-dashboard">
      <div className="flex items-stretch justify-between gap-3">
        <div className="min-w-0">
          <p className="overline">{T("My assignments")}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter" data-testid="worker-ticket-count">
            {tickets.length} {tickets.length === 1 ? T("ticket") : T("tickets")}
            <span className="text-muted-foreground font-normal text-base sm:text-lg ml-2">
              {T("across")} {colonies.length} {colonies.length === 1 ? T("colony") : T("colonies")}
            </span>
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {me?.is_worker_manager
              ? T("As a worker manager you see tickets from every colony.")
              : T("You see tickets from colonies where the office has added you as a watcher.")}
          </p>
        </div>
        <div className="flex flex-col justify-start items-end gap-1.5 shrink-0">
          <div className="flex items-center gap-1.5">
            {me && <TutorialButton audience="worker" section={me.is_worker_manager ? "manager" : "worker"} language={getLang()} className="rounded-sm gap-2" />}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={load}
              disabled={loading}
              title={T("Refresh")}
              aria-label={T("Refresh")}
              data-testid="worker-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 sm:mr-2 ${loading ? "animate-spin" : ""}`} /><span className="hidden sm:inline">{T("Refresh")}</span>
            </Button>
          </div>
          <div className="flex max-w-full items-center justify-end gap-1.5" data-testid="worker-ticket-top-actions">
          <CalendarYearSelect page={ticketPage} id="worker-tickets-year" T={T} onChange={() => { setDateFrom(""); setDateTo(""); }} />
          {colonies.length > 0 && (
            <Button
              size="sm"
              className="h-8 rounded-sm px-2.5 text-xs sm:h-9 sm:text-sm"
              onClick={() => setCreateOpen(true)}
              data-testid="worker-new-ticket"
            >
              <Plus className="w-3.5 h-3.5 mr-2" /> {T("New ticket")}
            </Button>
          )}
          </div>
        </div>
      </div>

      {loadError && !loading && (
        <Card className="p-8 text-center border-red-300 bg-red-50/60" data-testid="worker-load-error">
          <h3 className="font-display text-lg font-bold text-red-800">{T("Couldn't load your tickets")}</h3>
          <p className="text-sm text-red-700/90 mt-1">{loadError}</p>
          <Button variant="outline" size="sm" className="rounded-sm mt-3" onClick={load} data-testid="worker-load-retry">
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> {T("Try again")}
          </Button>
        </Card>
      )}

      {/* Only show the "no colonies" empty state when there are ALSO no
          tickets — a worker can have zero watched colonies but still be
          personally assigned tickets (e.g. auto tickets). */}
      {!loadError && colonies.length === 0 && tickets.length === 0 && !loading && (
        <Card className="p-8 text-center border-dashed" data-testid="worker-no-colonies">
          <h3 className="font-display text-lg font-bold">No colonies assigned yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            The office hasn&apos;t added you as a watcher on any bungalow colony. Once they do, tickets for those colonies will appear here.
          </p>
        </Card>
      )}

      {(colonies.length > 0 || tickets.length > 0 || ticketPage.year) && (
        <>
          {/* Renders in normal flow until scrolled up to the header, then
              switches to real `position: fixed` (a sentinel that mimics
              `sticky` via IntersectionObserver — plain CSS `sticky` here
              would hit a long-standing iOS Safari WebKit bug with two
              stacked/nested sticky elements, this bar + the header above,
              where the second one stops tracking scroll). Only fixed while
              actually stuck, so it never overlaps the "New ticket" button
              / helper text that sits above it before that. */}
          <div ref={sentinelRef} />
          <div
            ref={filterBarRef}
            className={isStuck
              ? "fixed inset-x-0 z-20 max-w-6xl mx-auto !mt-0 px-4 py-2 bg-emerald-50/95 backdrop-blur-sm"
              : "relative z-20 -mx-4 px-4 py-2 bg-emerald-50/95 backdrop-blur-sm"}
            style={isStuck ? { top: `var(${stickyOffsetVar}, 0px)` } : undefined}
          >
            <div className="flex flex-wrap items-end gap-2 pb-1 lg:pb-0" data-testid="worker-ticket-filter-controls">
              <TicketSearchControl
                search={search} setSearch={setSearch}
                dateFrom={dateFrom} setDateFrom={setDateFrom}
                dateTo={dateTo} setDateTo={setDateTo}
                testidPrefix="worker-tickets"
                responsive={isMobileViewport}
              />
              <div className="min-w-0 flex-1 basis-[240px] lg:flex-initial lg:basis-auto">
                <div className="hidden lg:block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{T("Status")}</div>
                <MobileStatusStrip id="worker-status-strip" label={T("Status")} T={T}>
                  <FilterPill
                    label={`${T("All")} (${counts.all || 0})`}
                    active={statusFilter === "all"}
                    onClick={() => setStatusFilter("all")}
                    testid="worker-status-filter-all"
                  />
                  {STATUS_ORDER.map((s) => (
                    <FilterPill
                      key={s}
                      label={`${T(STATUS_META[s].label)} (${counts[s] || 0})`}
                      active={statusFilter === s}
                      onClick={() => setStatusFilter(s)}
                      pillCls={STATUS_META[s].cls}
                      testid={`worker-status-filter-${s}`}
                    />
                  ))}
                </MobileStatusStrip>
              </div>
              {colonies.length > 1 && (
                <div className="shrink-0 w-[150px] lg:w-[220px]">
                  <div className="hidden lg:block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{T("Colony")}</div>
                  <Select value={colonyFilter} onValueChange={setColonyFilter}>
                    <SelectTrigger className="h-9 rounded-sm" data-testid="worker-colony-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{T("All colonies")}</SelectItem>
                      {colonies.map((c) => (
                        <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="shrink-0 w-[140px] lg:w-[190px]">
                <div className="hidden lg:block text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{T("Sort by")}</div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="worker-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">{T("Newest first")}</SelectItem>
                    <SelectItem value="oldest">{T("Oldest first")}</SelectItem>
                    <SelectItem value="updated">{T("Last modified or closed")}</SelectItem>
                    <SelectItem value="colony">{T("Bungalow colony")}</SelectItem>
                    <SelectItem value="scheduled">{T("Scheduled date")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {isStuck && <div style={{ height: filterBarHeight }} aria-hidden="true" />}

          {loading ? (
            <div className="text-sm text-muted-foreground py-6">{T("Loading tickets…")}</div>
          ) : filtered.length === 0 ? (
            <Card className="p-8 text-center border-dashed" data-testid="worker-tickets-empty">
              <h3 className="font-display text-lg font-bold">{T("Nothing here")}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {tickets.length === 0
                  ? "No tickets from your colonies yet."
                  : "No tickets match the current filters — try clearing them."}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((t) => (
                <WorkerTicketCard
                  key={t.id}
                  ticket={t}
                  isFocused={t.id === focusedTicketId}
                  me={me}
                  highlight={search}
                  atr={atr}
                  colonyName={colonyName(t.colony_id)}
                  onUpdated={(u) => {
                    setTickets((prev) => prev.map((x) => (x.id === u.id ? u : x)));
                    // Pressing "Start" (or resuming from hold) should show the
                    // worker where the ticket went, right away. A fresh
                    // reload/re-open of the app still defaults back to Open.
                    if (u.status === "in_progress" && statusFilter !== "all") {
                      setStatusFilter("in_progress");
                      setScrollToTicketId(u.id);
                    }
                  }}
                  onGoToShoppingList={me?.is_worker_manager === true ? () => setShoppingOpen(true) : undefined}
                />
              ))}
            </div>
          )}
        </>
      )}

      <WorkerCreateTicketDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        colonies={colonies}
        isManager={me?.is_worker_manager === true}
        onCreated={() => load()}
      />
      <WorkerExpensesDialog open={expensesOpen} onOpenChange={open => { setExpensesOpen(open); if (!open) setFocusExpenseId(""); }} focusExpenseId={focusExpenseId} />
      <Dialog open={shoppingOpen} onOpenChange={setShoppingOpen} modal={false}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="worker-shopping-dialog">
          <DialogHeader>
            <DialogTitle>{T("Shopping list")}</DialogTitle>
            <DialogDescription>{T("Products needed across your colonies.")}</DialogDescription>
          </DialogHeader>
          <ShoppingListView client={api} basePath="/worker" onOpenTicket={openTicketFromShopping} allColonies={colonies} T={T} />
        </DialogContent>
      </Dialog>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} client={api} base="/feedback" T={T} />
      <TicketsAiButton key={ticketPage.year || "loading"} calendarYear={ticketPage.year} client={api} askPath="/worker/tickets/ask" T={T} hidden={shoppingOpen || ticketPage.loading || !!loadError || !ticketPage.year} />
    </div>
  );
}

function FilterPill({ label, active, onClick, pillCls = "", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-xs font-semibold rounded-sm border transition ${
        active
          ? `${pillCls || "bg-primary text-primary-foreground border-primary"} shadow-sm`
          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function WorkerTicketCard({ ticket, colonyName, onUpdated, me = null, highlight = "", atr = (x) => x, onGoToShoppingList, isFocused = false }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  const isMine = !!me?.id && ticket.created_by_user_id === me.id;
  const [replyOpen, setReplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(ticket.status === "in_progress");
  const [threadOpen, setThreadOpen] = useState(ticket.status === "in_progress");
  useEffect(() => {
    if (isFocused) { setDetailsOpen(true); setThreadOpen(true); }
  }, [isFocused]);

  const saveEdit = async (p) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/worker/tickets/${ticket.id}`, p);
      onUpdated(data);
      toast.success("Ticket updated.");
      setEditOpen(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (next) => {
    if (ticket.status === next) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/worker/tickets/${ticket.id}`, { status: next });
      toast.success(`Marked as ${STATUS_META[next]?.label || next}.`);
      onUpdated(data);
    } catch (e) {
      if (queueIfOffline(e, { portal: "api", method: "patch", path: `/worker/tickets/${ticket.id}`, body: { status: next }, label: `Mark ticket "${ticket.title}" ${STATUS_META[next]?.label || next}` })) {
        onUpdated({ ...ticket, status: next });
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const isClosed = ticket.status === "done" || ticket.status === "cancelled";
  const urgentActive = ticket.urgent && (ticket.status === "open" || ticket.status === "in_progress");
  return (
    <div id={`worker-ticket-${ticket.id}`} data-focused={isFocused ? "true" : "false"} className={`border rounded-sm p-4 ${isFocused ? "ring-2 ring-emerald-500 ring-offset-2" : ""} ${urgentActive ? "border-red-300 bg-red-50/70" : "border-border bg-background"}`} data-testid={`worker-ticket-card-${ticket.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="min-w-0 text-base font-semibold [overflow-wrap:anywhere]"><Highlight text={atr(ticket.title)} query={highlight} /></h3>
            {ticket.urgent && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-red-400 bg-red-100 text-red-800 font-bold" data-testid={`worker-ticket-urgent-${ticket.id}`}>{T("Urgent")}</span>
            )}
            {ticket.require_live_photo && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-400 bg-emerald-50 text-emerald-800 font-bold" data-testid={`worker-ticket-live-required-${ticket.id}`}>{T("Live photo required")}</span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>
              {T(meta.label)}
            </span>
            {ticket.scheduled_for && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-purple-300 bg-purple-50 text-purple-800 inline-flex items-center gap-1" title={ticket.status === "scheduled" ? "Will be filed automatically at this date & time" : "Revisit on this date"}>
                <CalendarClock className="w-3 h-3" /> {fmtDateTime(ticket.scheduled_for)}
              </span>
            )}
          </div>
          <div className="mt-1 flex min-w-0 max-w-full flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <ColonyBadge id={ticket.colony_id} name={colonyName} />
            <span className="min-w-0 [overflow-wrap:anywhere]">{T("Filed")}{" "}<HoverTime iso={ticket.created_at} label="Filed " testId={`worker-ticket-filed-${ticket.id}`} />{" · "}{T("by")}{" "}<Highlight text={ticket.created_by_name || ticket.created_by_email} query={highlight} /></span>
          </div>
          <CollapseToggle
            open={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
            label={detailsOpen ? T("Hide details") : T("Show details")}
            testid={`worker-ticket-details-toggle-${ticket.id}`}
            className="mt-2"
          />
          {detailsOpen && ticket.description && (
            <p className="text-sm mt-2 whitespace-pre-wrap"><Highlight text={atr(ticket.description)} query={highlight} /></p>
          )}
          {/* Photos + shopping items always visible — never hidden behind the Details toggle. */}
          {(ticket.photos?.length || ticket.photo_count) ? (
            <div className="mt-3">
              <LazyPhotoStrip
                photos={ticket.photos}
                count={ticket.photo_count}
                fetchManifest={() => api.get(`/worker/tickets/${ticket.id}/photos/manifest`).then((r) => r.data.photos)}
                fetchPhoto={(index) => api.get(`/worker/tickets/${ticket.id}/photos/item?source=ticket&index=${index}`).then((r) => r.data.photo)}
                alt="ticket photo"
                testid={`worker-ticket-photo-${ticket.id}`}
                cacheKey={`ticket-${ticket.id}`}
              />
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
              <ImageOff className="w-3 h-3" /> {T("no photos attached")}
            </div>
          )}
          <TicketShoppingStrip
            count={ticket.shopping_count}
            fetchItems={() => api.get(`/worker/tickets/${ticket.id}/shopping`).then((r) => r.data)}
            client={api}
            basePath="/worker"
            T={T}
            testid={`worker-ticket-shopping-items-${ticket.id}`}
          />
        </div>
        {ticket.status !== "done" && ticket.status !== "cancelled" && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => setReplyOpen((v) => !v)}
            data-testid={`worker-reply-toggle-${ticket.id}`}
          >
            <Reply className="w-3.5 h-3.5 mr-2" /> {replyOpen ? T("Hide reply") : T("Add reply")}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-3" data-testid={`worker-actions-${ticket.id}`}>
        <div className="flex">
          <Button
            variant="outline"
            size="sm"
            className={`rounded-sm ${onGoToShoppingList ? "rounded-r-none" : ""}`}
            onClick={() => setShopOpen(true)}
            data-testid={`worker-shopping-${ticket.id}`}
          >
            <ShoppingCart className="w-3.5 h-3.5 mr-2" /> {T("Add to shopping list")}
          </Button>
          {onGoToShoppingList && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm rounded-l-none border-l-0 px-2 shrink-0"
              onClick={onGoToShoppingList}
              title={T("Go to shopping list")}
              data-testid={`worker-shopping-goto-${ticket.id}`}
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        {isMine && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            disabled={saving}
            onClick={() => setEditOpen(true)}
            data-testid={`worker-edit-${ticket.id}`}
          >
            <Pencil className="w-3.5 h-3.5 mr-2" /> {T("Edit")}
          </Button>
        )}
        {!isClosed && (
          <>
            {ticket.status !== "in_progress" && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-blue-300 bg-background text-blue-700 hover:bg-blue-50"
                disabled={saving}
                onClick={() => changeStatus("in_progress")}
                data-testid={`worker-mark-in-progress-${ticket.id}`}
              >
                <PlayCircle className="w-3.5 h-3.5 mr-2" /> {T("Start")}
              </Button>
            )}
            {ticket.status !== "hold" && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-purple-300 bg-background text-purple-700 hover:bg-purple-50"
                disabled={saving}
                onClick={() => changeStatus("hold")}
                data-testid={`worker-mark-hold-${ticket.id}`}
              >
                <PauseCircle className="w-3.5 h-3.5 mr-2" /> {T("Hold")}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-emerald-300 bg-background text-emerald-700 hover:bg-emerald-50"
              disabled={saving}
              onClick={() => setCloseOpen(true)}
              data-testid={`worker-mark-done-${ticket.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> {T("Close")}
            </Button>
          </>
        )}
        {isClosed && ticket.status === "done" && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-amber-300 bg-background text-amber-700 hover:bg-amber-50"
            disabled={saving}
            onClick={() => changeStatus("open")}
            data-testid={`worker-reopen-${ticket.id}`}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-2" /> {T("Reopen")}
          </Button>
        )}
      </div>

      {ticket.notes?.length > 0 && (
        <div className="mt-4 border-t border-border pt-3 space-y-2">
          <CollapseToggle
            open={threadOpen}
            onClick={() => setThreadOpen((v) => !v)}
            label={`${T("Replies")} (${ticket.notes.length})`}
            testid={`worker-ticket-thread-toggle-${ticket.id}`}
          />
          {threadOpen && ticket.notes.map((n) => {
            const isColony = n.author === "colony";
            return (
              <div
                key={n.id}
                className={`pl-3 py-2 rounded-r-sm border-l-2 ${
                  isColony ? "bg-slate-50 border-slate-400" : "bg-emerald-50 border-emerald-600"
                }`}
              >
                <div className="text-xs text-muted-foreground">
                  <strong className="text-foreground">
                    {isColony ? (n.author_name || "Colony contact") : (n.author_name || "Office")}
                  </strong>
                  {" · "}<HoverTime iso={n.created_at} testId={`worker-ticket-note-time-${n.id}`} />
                </div>
                {n.body && <p className="text-sm mt-1 whitespace-pre-wrap"><Highlight text={atr(n.body)} query={highlight} /></p>}
                {(n.photos?.length || n.photo_count) ? (
                  <div className="mt-2">
                    <LazyPhotoStrip
                      photos={n.photos}
                      count={n.photo_count}
                      fetchManifest={() => api.get(`/worker/tickets/${ticket.id}/photos/manifest`).then((r) => (r.data.note_photos || {})[n.id] || [])}
                      fetchPhoto={(index) => api.get(`/worker/tickets/${ticket.id}/photos/item?source=note&note_id=${encodeURIComponent(n.id)}&index=${index}`).then((r) => r.data.photo)}
                      alt="update photo"
                      size="w-14 h-14"
                      cacheKey={`note-${n.id}`}
                      testid={`worker-note-photo-${n.id}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {replyOpen && (
        <WorkerReplyForm
          ticketId={ticket.id}
            colonyId={ticket.colony_id}
          onDone={(updated) => { setReplyOpen(false); onUpdated(updated); }}
        />
      )}

      <WorkerCloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        ticket={ticket}
        onSkip={() => { setCloseOpen(false); changeStatus("done"); }}
        onClosed={(updated) => { setCloseOpen(false); onUpdated(updated); }}
      />
      <TicketEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        saving={saving}
        onSave={saveEdit}
      />
      <AddToShoppingDialog
        open={shopOpen}
        onOpenChange={setShopOpen}
        client={api}
        path={`/worker/tickets/${ticket.id}/shopping`}
        ticketTitle={ticket.title}
        T={T}
      />
    </div>
  );
}

/**
 * Shown when a worker hits Close: asks for photos of the completed work
 * (+ optional note) — with a "Skip & close" escape hatch. Mirrors the
 * admin close-with-photos flow via POST /worker/tickets/{id}/notes with
 * close_ticket=true.
 */
function WorkerCloseDialog({ open, onOpenChange, ticket, onSkip, onClosed }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const needsLive = ticket.require_live_photo === true;

  useEffect(() => {
    if (open) { setPhotos([]); setNote(""); }
  }, [open]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId: ticket?.colony_id });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const closeWithPhotos = () => {
    if (needsLive && !photos.some(isLivePhoto)) {
      return toast.error("This ticket requires a Live photo — tap the green LIVE button to take one.");
    }
    if (!photos.length && !note.trim()) {
      return toast.error("Add a photo or note — or use Skip & close.");
    }
    // Optimistic close: the UI updates instantly, photos upload in the
    // background. On failure the ticket is restored and the worker told.
    const payload = { body: note.trim(), photos, close_ticket: true };
    onClosed({ ...ticket, status: "done" });
    toast.success(photos.length ? "Ticket closed — photos uploading in the background…" : "Ticket closed.");
    api.post(`/worker/tickets/${ticket.id}/notes`, payload)
      .then(({ data }) => onClosed(data))
      .catch((e) => {
        if (queueIfOffline(e, { portal: "api", method: "post", path: `/worker/tickets/${ticket.id}/notes`, body: payload, label: `Close ticket "${ticket.title}"` })) return;
        onClosed(ticket); // restore — nothing was saved server-side
        toast.error(
          "Couldn't save the closing photos — the ticket was NOT closed. " +
          (formatErr(e.response?.data?.detail) || e.message)
        );
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="max-w-md" data-testid={`worker-close-dialog-${ticket.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" /> {T("Close ticket")}
          </DialogTitle>
          <DialogDescription>
            Attach photos of the completed work for &quot;{ticket.title}&quot;
            {needsLive ? (
              <span className="block mt-1 font-semibold text-emerald-700" data-testid={`worker-close-live-required-${ticket.id}`}>
                This ticket requires at least one Live photo (tap the green LIVE button).
              </span>
            ) : (
              <> — or skip and close without photos.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`worker-close-preview-${ticket.id}-${i}`} />
                <button
                  type="button"
                  onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))}
                  className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center"
                  aria-label="Remove photo"
                  data-testid={`worker-close-photo-remove-${ticket.id}-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {photos.length < 6 && (
              <PhotoAddButtons onFiles={addFiles} testidPrefix={`worker-close-${ticket.id}`} />
            )}
          </div>
          <Textarea
            placeholder={T("Optional closing note…  (you can also paste a picture here)")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onPaste={pasteToFiles(addFiles)}
            rows={2}
            className="rounded-sm"
            data-testid={`worker-close-note-${ticket.id}`}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid={`worker-close-cancel-${ticket.id}`}>
            {T("Cancel")}
          </Button>
          {!needsLive && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={onSkip} data-testid={`worker-close-skip-${ticket.id}`}>
              {T("Skip & close")}
            </Button>
          )}
          <Button
            size="sm"
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={closeWithPhotos}
           
            data-testid={`worker-close-with-photos-${ticket.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> {T("Close with photos")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function WorkerReplyForm({ ticketId, colonyId, onDone }) {
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const submit = async () => {
    const text = body.trim();
    if (!text && photos.length === 0) return toast.error("Add a note, a photo, or both.");
    setSaving(true);
    try {
      const { data } = await api.post(`/worker/tickets/${ticketId}/notes`, { body: text, photos });
      const nAdded = await submitShoppingItems(api, `/worker/tickets/${ticketId}/shopping`, shopItems);
      toast.success(T("Reply sent to the colony."));
      if (nAdded) toast.success(`${nAdded} ${nAdded === 1 ? T("product added to shopping list.") : T("products added to shopping list.")}`);
      onDone(data);
    } catch (e) {
      if (queueIfOffline(e, { portal: "api", method: "post", path: `/worker/tickets/${ticketId}/notes`, body: { body: text, photos }, label: "Send reply" })) {
        setBody("");
        setPhotos([]);
        return;
      }
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3 space-y-2" data-testid={`worker-reply-form-${ticketId}`}>
      <Textarea
        placeholder={T("Update the colony…  (you can also paste a picture here)")}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onPaste={pasteToFiles(addFiles)}
        rows={3}
        className="rounded-sm"
        data-testid={`worker-reply-body-${ticketId}`}
      />
      <div className="flex flex-wrap gap-2 items-center">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`worker-reply-preview-${ticketId}-${i}`} />
            <button
              type="button"
              onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
              className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center"
              aria-label="Remove photo"
              data-testid={`worker-ticket-reply-photo-remove-${ticketId}-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < 6 && (
          <PhotoAddButtons onFiles={addFiles} testidPrefix={`worker-reply-${ticketId}`} />
        )}
      </div>
      <ReplyShoppingSection items={shopItems} setItems={setShopItems} T={T} />
      <div className="flex justify-end">
        <Button
          size="sm"
          className="rounded-sm"
          onClick={submit}
          disabled={saving}
          data-testid={`worker-reply-submit-${ticketId}`}
        >
          <Reply className="w-3.5 h-3.5 mr-2" /> {saving ? T("Sending…") : T("Send reply")}
        </Button>
      </div>
    </div>
  );
}


function WorkerCreateTicketDialog({ open, onOpenChange, colonies, isManager = false, onCreated }) {
  const [colonyId, setColonyId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [photos, setPhotos] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [urgent, setUrgent] = useState(false);
  useEffect(() => { if (open) setUrgent(false); }, [open]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Default to the first available colony when the dialog opens.
  useEffect(() => {
    if (open && !colonyId && colonies.length > 0) {
      setColonyId(colonies[0].id);
    }
    if (open && isManager) {
      api.get("/worker/assignable-workers").then((r) => setAssignable(r.data || [])).catch(() => {});
    }
    if (!open) {
      setTitle(""); setDescription(""); setScheduledFor(""); setPhotos([]); setAssignees([]); setAssignOpen(false); setDateOpen(false);
    }
  }, [open, colonies, colonyId, isManager]);

  const addFiles = async (files) => {
    if (!files) return;
    try {
      const result = await processMediaFiles(files, { max: 6, existingCount: photos.length, client: api, scope: "mgmt_usa_nj", colonyId });
      setPhotos((current) => [...current, ...result.items].slice(0, 6));
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) { toast.error(error.message || "Could not add that photo."); }
  };

  const submit = async () => {
    const t = title.trim();
    if (!colonyId) return toast.error("Pick a colony.");
    if (!t) return toast.error("Give the ticket a short title.");
    setSaving(true);
    const body = {
      colony_id: colonyId,
      title: t,
      description: description.trim(),
      photos,
      scheduled_for: scheduledFor || null,
      urgent,
      ...(isManager && assignees.length ? { assignees } : {}),
    };
    try {
      const { data } = await api.post("/worker/tickets", body);
      toast.success(T("Ticket created."));
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      if (queueIfOffline(e, { portal: "api", method: "post", path: "/worker/tickets", body, label: `Create ticket "${t}"` })) {
        onOpenChange(false);
      } else {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="worker-new-ticket-dialog">
        <DialogHeader>
          <DialogTitle>{T("New service ticket")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{T("Colony")}</Label>
            <Select value={colonyId} onValueChange={setColonyId}>
              <SelectTrigger className="rounded-sm" data-testid="worker-new-ticket-colony">
                <SelectValue placeholder={T("Pick a colony…")} />
              </SelectTrigger>
              <SelectContent>
                {colonies.map((c) => (
                  <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{T("Title")}</Label>
            <Input
              placeholder={T("e.g. Fix leaking pipe at Unit 12")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="rounded-sm"
              data-testid="worker-new-ticket-title"
            />
          </div>
          <div>
            <Label>{T("Details")}</Label>
            <Textarea
              rows={3}
              placeholder={T("What did you find? What needs to happen?  (you can also paste a picture here)")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={pasteToFiles(addFiles)}
              className="rounded-sm"
              data-testid="worker-new-ticket-desc"
            />
          </div>
          <label className="flex items-center gap-2 border border-red-200 bg-red-50/60 rounded-sm px-3 py-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
              className="w-4 h-4 accent-red-600"
              data-testid="worker-new-ticket-urgent"
            />
            <span className="text-sm font-semibold text-red-700">{T("Mark as urgent")}</span>
            <span className="text-xs text-red-600/70 ml-auto">{T("Shows red & jumps to the top")}</span>
          </label>
          {!dateOpen ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-sm px-2 text-muted-foreground"
              onClick={() => setDateOpen(true)}
              data-testid="worker-new-ticket-schedule-toggle"
            >
              <CalendarClock className="w-3.5 h-3.5 mr-2" /> {T("Schedule for later (optional)")}
            </Button>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label>{T("Date & time")}</Label>
                <Input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="rounded-sm"
                  data-testid="worker-new-ticket-date"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm text-muted-foreground"
                onClick={() => { setScheduledFor(""); setDateOpen(false); }}
                data-testid="worker-new-ticket-schedule-clear"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Remove date
              </Button>
              <p className="text-xs text-muted-foreground w-full">
                Pick a future date &amp; time and the ticket stays &quot;Scheduled&quot; (only you and the office see it) until it goes live.
              </p>
            </div>
          )}
          {isManager && assignable.length > 0 && (
            !assignOpen ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm px-2 text-muted-foreground"
                onClick={() => setAssignOpen(true)}
                data-testid="worker-new-ticket-assign-toggle"
              >
                <UserPlus className="w-3.5 h-3.5 mr-2" /> {T("Assign to specific people (optional)")}
              </Button>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <Label>{T("Assign to")}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-sm px-2 text-muted-foreground h-7"
                    onClick={() => { setAssignees([]); setAssignOpen(false); }}
                    data-testid="worker-new-ticket-assign-clear"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Everyone instead
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-1">Only the people you pick will see this ticket and get emailed.</p>
                <div className="border border-border rounded-sm max-h-36 overflow-y-auto" data-testid="worker-new-ticket-assignees">
                  {assignable.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/40">
                      <input
                        type="checkbox"
                        checked={assignees.includes(u.id)}
                        onChange={() => setAssignees((prev) => prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id])}
                        data-testid={`worker-new-ticket-assignee-${u.id}`}
                      />
                      {u.name || u.email}
                    </label>
                  ))}
                </div>
              </div>
            )
          )}
          <div>
            <Label>{T("Photos (up to 6)")}</Label>
            <div className="flex flex-wrap gap-2 items-center mt-1">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <LivePhotoSurface value={p} alt={`upload ${i + 1}`} className="w-14 h-14 rounded-sm border border-border" testId={`worker-new-ticket-preview-${i}`} />
                  <button
                    type="button"
                    onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 bg-background border border-border rounded-full w-5 h-5 flex items-center justify-center"
                    aria-label="Remove photo"
                    data-testid={`worker-new-ticket-photo-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 6 && (
                <PhotoAddButtons onFiles={addFiles} testidPrefix="worker-new-ticket" />
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="worker-new-ticket-cancel">
            {T("Cancel")}
          </Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="worker-new-ticket-submit">
            {saving ? T("Creating…") : T("Create ticket")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
